import { FC, useContext, useState } from "react";
import { Button, Dialog } from "@mui/material";
import { Cell, CellType, CellValueCountsView, MigrateLiveCellTypeDocument } from "generated/graphql";
import { LoadingButton } from "@mui/lab";
import RequestStatus from "redux/slices/RequestStatus";
import { useSnackbar } from "notistack";
import { runGqlMutation } from "gqlClient";
import { LensUpdateSnackbarContext } from "contexts/LensUpdateSnackbarContext";
import { useAppSelector } from "redux/hooks";
import { selectLensEditorLensId } from "redux/slices/lensEditor";

type MigrateLiveCellDialogProps = {
  onClose: () => void;
  cell: Cell;
  onSuccess: () => void;
  cellValueCounts: CellValueCountsView[];
  title: string;
  confirmation: string;
  newCellType: CellType;
};

const MigrateLiveCellDialog: FC<MigrateLiveCellDialogProps> = ({
  onClose,
  cell,
  newCellType,
  onSuccess,
  title,
  confirmation,
}) => {
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const { enqueueSnackbar } = useSnackbar();
  const lensId = useAppSelector(selectLensEditorLensId);
  const { enqueueLensUpdateTimerSnackbar } = useContext(LensUpdateSnackbarContext);

  const startMigration = (cellId: number) => {
    setStatus(RequestStatus.PENDING);
    runGqlMutation(MigrateLiveCellTypeDocument, {
      cellId,
      newCellType,
    })
      .then(() => {
        setStatus(RequestStatus.SUCCEEDED);
        enqueueSnackbar(`Successfully migrated cell.`, {
          variant: "success",
        });
        onSuccess();
        enqueueLensUpdateTimerSnackbar(lensId);
      })
      .catch(() => {
        setStatus(RequestStatus.FAILED);
        enqueueSnackbar(`Operation failed`, {
          variant: "error",
        });
      });
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <div className="p-4 w-96 mt-1">
        <div className="mb-2">
          <div className="grid gap-2">
            <p className="text-xl font-semibold">{title}</p>
            <p className="text-sm">{confirmation}</p>
          </div>
        </div>
        <div className="flex justify-end items-center gap-1">
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={status === RequestStatus.PENDING}
            disabled={status === RequestStatus.PENDING}
            onClick={() => {
              startMigration(cell.id);
            }}>
            Migrate Cell
          </LoadingButton>
        </div>
      </div>
    </Dialog>
  );
};

export default MigrateLiveCellDialog;
